import React, { useEffect, useState } from 'react';
import style from '../assets/styles/WelcomeSaluteForm.module.scss';
import Title from '../component/title.js';
import { db } from '../config-firebase/firebase.js';
import { doc, getDoc } from 'firebase/firestore';
import { setDoc } from '../api/FirestoreInterceptor';
import { Link, useNavigate } from 'react-router-dom';

const DEFAULT_MESSAGES = {
  default: {
    title: 'Opção 1 (Padrão do Sistema - Atendimento na Mesa)',
    primary: 'O Garçom já está a caminho com a máquina para finalizar o seu pagamento na mesa.',
    secondary: 'Por favor, aguarde o fechamento da conta.',
    tag: 'Padrão Recomendado',
  },
  cashier: {
    title: 'Opção 2 (Pagamento no Caixa / Balcão)',
    primary: 'Por favor, dirija-se ao caixa para realizar o pagamento e informe o seu nome e o número da sua mesa.',
    secondary: 'Por favor, aguarde o atendimento no caixa.',
    tag: 'Pagamento no Caixa',
  },
};

function WelcomeSaluteForm() {
  const navigate = useNavigate();

  const [form, setForm] = useState({
    welcomeMessage: '',
    rewardDescription: '',
  });

  const [billClosingOption, setBillClosingOption] = useState('default');
  const [customInput, setCustomInput] = useState('');
  const [savedCustomMessage, setSavedCustomMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
    let isMounted = true;

    async function loadConfig() {
      try {
        const docRef = doc(db, 'welcomeCustomer', 'Zju4GZbnQFaq4fWjGch1');
        const docSnap = await getDoc(docRef);

        if (docSnap.exists() && isMounted) {
          const data = docSnap.data();
          setForm({
            welcomeMessage: data.welcomeMessage || '',
            rewardDescription: data.rewardDescription || '',
          });

          const currentOption = data.billClosingOption || 'default';
          const customMsg = data.customBillClosingMessage || '';

          setBillClosingOption(currentOption);
          setSavedCustomMessage(customMsg);
          setCustomInput(customMsg);
        }
      } catch (error) {
        console.error('Erro ao carregar configurações de mensagens:', error);
      } finally {
        if (isMounted) setIsLoading(false);
      }
    }

    loadConfig();

    return () => {
      isMounted = false;
    };
  }, []);

  function handleFormChange({ target }) {
    const { id, value } = target;
    setForm((prev) => ({ ...prev, [id]: value }));
  }

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSaving(true);
    setSaveSuccess(false);

    try {
      const finalCustomMessage = customInput.trim() || savedCustomMessage;

      const payload = {
        welcomeMessage: form.welcomeMessage,
        rewardDescription: form.rewardDescription,
        billClosingOption: billClosingOption,
        customBillClosingMessage: finalCustomMessage,
      };

      await setDoc(doc(db, 'welcomeCustomer', 'Zju4GZbnQFaq4fWjGch1'), payload);

      setSavedCustomMessage(finalCustomMessage);
      setCustomInput(finalCustomMessage);
      setSaveSuccess(true);

      setTimeout(() => {
        setSaveSuccess(false);
      }, 4000);
    } catch (error) {
      console.error('Erro ao salvar configurações:', error);
      alert('Erro ao salvar as configurações. Tente novamente.');
    } finally {
      setIsSaving(false);
    }
  };

  const getPreviewMessage = () => {
    if (billClosingOption === 'cashier') {
      return {
        primary: DEFAULT_MESSAGES.cashier.primary,
        secondary: DEFAULT_MESSAGES.cashier.secondary,
      };
    }

    if (billClosingOption === 'custom') {
      const text = customInput.trim() || savedCustomMessage || 'Personalize a sua mensagem digitando no campo acima...';
      return {
        primary: text,
        secondary: '',
      };
    }

    return {
      primary: DEFAULT_MESSAGES.default.primary,
      secondary: DEFAULT_MESSAGES.default.secondary,
    };
  };

  const preview = getPreviewMessage();

  return (
    <div className={style.containerWelcomeSalute}>
      <div className={style.containerIcon}>
        <a
          href="https://docs.google.com/document/d/1JO_71SmMvI_lkzAerER1YuuM_F-0Sdp6-dJrdy7E1oQ/edit?tab=t.24804x2kn895"
          target="_blank"
          rel="noopener noreferrer"
          title="Abrir documentação"
        >
          <span>?</span>
        </a>
      </div>

      <Link to="/admin/admin" className={style.btnBack} title="Sair do Módulo">
        <svg viewBox="0 0 24 24" fill="none" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M19 12H5M12 19l-7-7 7-7" />
        </svg>
      </Link>

      <Link to="/admin/admin" style={{ textDecoration: 'none' }}>
        <Title mainTitle="Mensagens do Sistema" />
      </Link>
      <Title title="Configurações de Boas-Vindas e Fechamento de Conta" />

      {saveSuccess && (
        <div className={style.successAlert}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12" />
          </svg>
          Configurações salvas com sucesso no sistema!
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* SEÇÃO 1: BOAS-VINDAS E BRINDE */}
        <div className={style.cardSection}>
          <h3 className={style.sectionTitle}>
            👋 Boas-Vindas e Incentivo ao Cadastro
          </h3>
          <p className={style.sectionSubtitle}>
            Configure a primeira impressão do cliente ao abrir o cardápio e a recompensa para cadastro de dados.
          </p>

          <div className={style.containerTextArea}>
            <div className={style.fieldGroup}>
              <label htmlFor="welcomeMessage">Mensagem de Saudação</label>
              <textarea
                id="welcomeMessage"
                className={style.textArea}
                value={form.welcomeMessage}
                onChange={handleFormChange}
                placeholder="Ex: Seja bem-vindo ao nosso restaurante!"
                title="Esta mensagem aparecerá no topo da tela de login/cadastro para todos os clientes."
                disabled={isLoading}
              />
            </div>

            <div className={style.fieldGroup}>
              <label htmlFor="rewardDescription">Descrição do Brinde (Incentivo ao Cadastro)</label>
              <textarea
                id="rewardDescription"
                className={style.textArea}
                value={form.rewardDescription}
                onChange={handleFormChange}
                placeholder="Ex: Faça seu cadastro agora e ganhe uma sobremesa grátis!"
                title="Use este campo para oferecer um presente ao cliente em troca dos dados dele."
                disabled={isLoading}
              />
            </div>
          </div>
        </div>

        {/* SEÇÃO 2: CAMPO DE FECHAMENTO DE CONTA */}
        <div className={style.cardSection}>
          <h3 className={style.sectionTitle}>
            🧾 Mensagem de Fechamento de Conta
          </h3>
          <p className={style.sectionSubtitle}>
            Selecione a mensagem que deve aparecer ao seu cliente na hora de pagar. Você pode escolher uma das opções prontas ou criar uma mensagem personalizada.
          </p>

          <div className={style.instructionTitle}>
            Selecione a mensagem que deve aparecer ao seu cliente na hora de pagar:
          </div>

          <div className={style.optionsList}>
            {/* OPÇÃO 1: PADRÃO */}
            <div
              className={`${style.optionCard} ${billClosingOption === 'default' ? style.selected : ''}`}
              onClick={() => setBillClosingOption('default')}
            >
              <div className={style.checkIndicator}>
                <div className={style.dot} />
              </div>
              <div className={style.optionBody}>
                <div className={style.optionHeaderRow}>
                  <span className={style.optionTitle}>Opção 1 (Padrão do Sistema)</span>
                  <span className={`${style.badge} ${billClosingOption === 'default' ? style.badgeActive : ''}`}>
                    Atendimento na Mesa
                  </span>
                </div>
                <p className={style.optionTextPreview}>
                  {DEFAULT_MESSAGES.default.primary}
                  <br />
                  <span style={{ opacity: 0.85, fontSize: '0.88rem' }}>
                    {DEFAULT_MESSAGES.default.secondary}
                  </span>
                </p>
              </div>
            </div>

            {/* OPÇÃO 2: IR AO CAIXA */}
            <div
              className={`${style.optionCard} ${billClosingOption === 'cashier' ? style.selected : ''}`}
              onClick={() => setBillClosingOption('cashier')}
            >
              <div className={style.checkIndicator}>
                <div className={style.dot} />
              </div>
              <div className={style.optionBody}>
                <div className={style.optionHeaderRow}>
                  <span className={style.optionTitle}>Opção 2 (Pagamento no Caixa)</span>
                  <span className={`${style.badge} ${billClosingOption === 'cashier' ? style.badgeActive : ''}`}>
                    Dirija-se ao Caixa
                  </span>
                </div>
                <p className={style.optionTextPreview}>
                  {DEFAULT_MESSAGES.cashier.primary}
                  <br />
                  <span style={{ opacity: 0.85, fontSize: '0.88rem' }}>
                    {DEFAULT_MESSAGES.cashier.secondary}
                  </span>
                </p>
              </div>
            </div>

            {/* OPÇÃO 3: MENSAGEM PERSONALIZADA */}
            <div
              className={`${style.optionCard} ${billClosingOption === 'custom' ? style.selected : ''}`}
              onClick={() => setBillClosingOption('custom')}
            >
              <div className={style.checkIndicator}>
                <div className={style.dot} />
              </div>
              <div className={style.optionBody}>
                <div className={style.optionHeaderRow}>
                  <span className={style.optionTitle}>Opção 3 - Personalize a sua mensagem</span>
                  <span className={`${style.badge} ${billClosingOption === 'custom' ? style.badgeActive : ''}`}>
                    Personalizada
                  </span>
                </div>
                <p className={style.optionTextPreview} style={{ color: '#475569' }}>
                  Escreva abaixo o texto que você deseja que o cliente leia no momento de fechar a conta:
                </p>

                <div
                  className={style.customTextareaContainer}
                  onClick={(e) => {
                    e.stopPropagation();
                    setBillClosingOption('custom');
                  }}
                >
                  <textarea
                    className={style.customTextarea}
                    value={customInput}
                    onChange={(e) => {
                      setCustomInput(e.target.value);
                      if (billClosingOption !== 'custom') {
                        setBillClosingOption('custom');
                      }
                    }}
                    placeholder="Digite a mensagem personalizada aqui... (Ex: Favor dirigir-se ao caixa com a comanda em mãos)"
                    rows={3}
                  />
                </div>

                {/* MENSAGEM VIGENTE ABAIXO DAS OUTRAS DUAS */}
                {savedCustomMessage ? (
                  <div className={style.currentCustomCard}>
                    <div className={style.currentCustomHeader}>
                      📌 Mensagem personalizada vigente no sistema:
                    </div>
                    <p className={style.currentCustomText}>
                      "{savedCustomMessage}"
                    </p>
                  </div>
                ) : (
                  <div className={style.currentCustomCard} style={{ borderLeftColor: '#94a3b8' }}>
                    <div className={style.currentCustomHeader} style={{ color: '#64748b' }}>
                      ℹ️ Nenhuma mensagem personalizada salva ainda
                    </div>
                    <p className={style.currentCustomText} style={{ color: '#64748b' }}>
                      Preencha o campo acima e clique em Salvar para definir a sua mensagem personalizada vigente.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* PRÉ-VISUALIZAÇÃO EM TEMPO REAL NA TELA DO CLIENTE */}
          <div className={style.livePreviewSection}>
            <div className={style.previewTitle}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                <circle cx="12" cy="12" r="3" />
              </svg>
              Pré-visualização da tela do cliente (Extrato do Pedido):
            </div>

            <div className={style.simulatedDevice}>
              <div className={style.simulatedHeadingTop}>
                EXTRATO DO PEDIDO
              </div>
              <div className={style.simulatedHeadingSent}>
                Solicitação Enviada!
              </div>
              <div className={style.simulatedPrimaryText}>
                {preview.primary}
              </div>
              {preview.secondary && (
                <div className={style.simulatedSecondaryText}>
                  {preview.secondary}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* BOTÃO SALVAR */}
        <div className={style.actionButtons}>
          <button
            type="submit"
            className={style.btnSave}
            disabled={isSaving || isLoading}
          >
            {isSaving ? 'Salvando...' : 'Salvar Configurações'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default WelcomeSaluteForm;
